import { Injectable, NgZone, signal, computed, Signal } from "@angular/core";
import { CookieChangeEvent, CookieChangeType } from "@apps/patient-core/types";
import { logger } from "@dentally/ui/logger";

/**
 * ConsentService
 *
 * This service tracks user consent via the OneTrust 'OneTrustGroupsUpdated' event.
 *
 * OneTrust cookie groups (example mapping):
 *
 * C0001 - Essential cookies
 *          • Required for the website to function
 *          • Examples: session management, authentication, site preferences
 *
 * C0002 - Performance / Analytics cookies
 *          • Used for analytics, performance monitoring, and user behavior tracking
 *          • Examples: Google Analytics, New Relic, Mixpanel
 *
 * C0003 - Functional cookies
 *          • Enable additional functionality / personalization
 *          • Examples: chat widgets, embedded videos
 *
 * C0004 - Targeting / Advertising cookies
 *          • Used for marketing and targeted advertising
 *          • Examples: remarketing, social media ads
 *
 */

enum E_CookieGroup {
  ESSENTIAL = "C0001",
  PERFORMANCE = "C0002",
  // FUNCTIONAL = "C0003", //not used or set by onetrust currently
  TARGETING = "C0004",
}

function parseCookie(cookieString: string): Record<string, number> | undefined {
  const groupsString = cookieString
    .split("&")
    .find((c) => c.startsWith("groups="))
    ?.split("=")[1];

  if (!groupsString) {
    return;
  }

  const decoded = decodeURIComponent(groupsString);

  const result = {};
  decoded.split(",").forEach((pair) => {
    const [key, value] = pair.split(":");
    result[key] = Number(value); // convert to number
  });

  return result;
}

@Injectable({ providedIn: "root" })
export class ConsentService {
  private _isPerformanceAllowed = signal(false);
  private _isTargetingAllowed = signal(false);

  public readonly performanceAllowed: Signal<boolean> = computed(() => this._isPerformanceAllowed());
  public readonly targetingAllowed: Signal<boolean> = computed(() => this._isTargetingAllowed());

  constructor(private _zone: NgZone) {
    if (!window.cookieStore) {
      logger.warn("cookieStore not supported", "bfafe67b");
      return;
    }

    this._zone.runOutsideAngular(() => {
      try {
        window.cookieStore.addEventListener("change", (event: CookieChangeEvent) => {
          this._zone.run(() => {
            event.changed.forEach((change: CookieChangeType) => {
              if (change.name === "OptanonConsent") {
                const groupsString = parseCookie(change.value);
                this._handleGroups(groupsString);
              }
            });
          });
        });
      } catch (error) {
        logger.error("Error adding cookieStore listener", "87fff101", error);
      }
    });
  }

  private _handleGroups(groups: Record<string, number> | undefined): void {
    if (!groups) {
      //Shouldn't happen, but to be safe assume no consent
      logger.warn("OptanonConsent cookie did not have groups value");
      this._isPerformanceAllowed.set(false);
      this._isTargetingAllowed.set(false);
      return;
    }

    this._isPerformanceAllowed.set(groups[E_CookieGroup.PERFORMANCE] === 1);
    this._isTargetingAllowed.set(groups[E_CookieGroup.TARGETING] === 1);
  }
}
